import React, { useRef, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Dialog,
  Fade,
  alpha,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getCrmMeta } from "../../../utils/crmUtils";
import { selectCrmCatalog } from "../../../state/integration/integrationSlice";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DialpadIcon from "@mui/icons-material/Dialpad";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CallEndIcon from "@mui/icons-material/CallEnd";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import withActiveCallManagement from "./withActiveCallManagement";
import Draggable from "react-draggable";
import DialpadOverlay from "./DialPadOverlay";
import CallParkOverlay from "./CallParkOverlay";
import CallHeader from "./CallHeader";
import ConferenceCallHeader from "./ConferenceCallHeader";
import FloatingAssistantOverlay from "./FloatingAssistantOverlay";
import CrmContactCard from "./CrmContactCard";
import LeadContextPanel from "./LeadContextPanel";

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "disabled",
})(({ theme, isActive, disabled }) => ({
  width: "60px",
  height: "60px",
  borderRadius: "8px",
  fontSize: "15px",
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? "none" : "auto",
  color: isActive
    ? theme.palette.background.layer1
    : theme.palette.common.white,
  backgroundColor: isActive
    ? theme.palette.common.white
    : theme.palette.background.layer1,
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.common.white
      : theme.palette.action.hover,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "30px",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  color: theme.palette.common.white,
  backgroundColor: theme.palette.error.main,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
    transform: "translateX(-50%) translateY(-5px)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "30px",
  },
}));

const MinimizeButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  top: "10px",
  right: "10px",
  minWidth: "unset",
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  padding: 0,
  zIndex: theme.zIndex.tooltip,
  backgroundColor: theme.palette.background.layer1,
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MinimizedContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: "20px",
  right: "20px",
  padding: "10px",
  backgroundColor: alpha(theme.palette.background.paper, 0.4),
  backdropFilter: "blur(8px)",
  borderRadius: "8px",
  boxShadow: theme.shadows[4],
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
}));

const ActiveCallScreen = ({
  activeCall,
  callSessions,
  isVisible,
  isMinimized,
  onCallEnd,
  toggleMinimize,
  onMuteToggle,
  onHoldToggle,
  onRecordingToggle,
  callAssistantEnabled,
  crmType,
  callQuotaStatus,
  onRealtimeToggle,
  onSendPrompt,
  aiAssistantEvents,
  crmPanelOpen,
  onCrmPanelToggle,
  leadContext,
  onSwapCalls,
  onMergeCalls,
  position,
  onDrag,
  drawerWidth,
  appBarHeight,
  openDialpad,
  isDialpadOpen,
  closeDialpad,
  dialpadMode,
  onAssistedTransfer,
  onCompleteTransfer,
  onCancelTransfer,
  openParkOverlay,
  isParkOverlayOpen,
  closeParkOverlay,
}) => {
  const theme = useTheme();
  const crmCatalog = useSelector(selectCrmCatalog);
  const nodeRef = useRef(null);
  const crmCardRef = useRef(null);
  const isOnHold = activeCall?.isOnHold || false;
  const isMuted = activeCall?.isMuted || false;
  const isRecording = activeCall?.isRecording || false;
  const isRealtime = activeCall?.isRealtime || false;
  const isStartingRealtime = activeCall?.isStartingRealtime || false;
  const isCallAccepted = activeCall?.status === "accepted";
  const isConference = activeCall?.isConference;
  const isMultipleCalls = callSessions.length > 1;
  const isTransferTarget = activeCall?.transferState?.isTransferTarget || false;

  const isQuotaExceeded = callQuotaStatus && !callQuotaStatus.allowed;

  const getAiAssistantTooltip = () => {
    if (!isCallAccepted) return "";

    if (!callAssistantEnabled) {
      return "AI Assistant is not enabled. Contact your administrator.";
    }

    if (isQuotaExceeded) {
      const reason = callQuotaStatus.reason;
      if (reason === "domain_disabled" || reason === "user_disabled") {
        return "AI Assistant is not enabled. Contact your administrator.";
      } else if (reason === "domain_quota_exceeded") {
        return "Your organization has exceeded its monthly AI assistant quota.";
      } else if (reason === "user_quota_exceeded") {
        return "You have exceeded your monthly AI assistant quota.";
      }
      return "AI Assistant quota exceeded.";
    }

    return "";
  };

  const buttonConfig = [
    {
      icon: isMuted ? <MicIcon /> : <MicOffIcon />,
      label: isMuted ? "Unmute" : "Mute",
      onClick: onMuteToggle,
      isActive: isMuted,
      disabled: false,
    },

    {
      icon: isOnHold ? <PlayArrowIcon /> : <PauseIcon />,
      label: isOnHold ? "Unhold" : "Hold",
      onClick: onHoldToggle,
      isActive: isOnHold,
      disabled: !isCallAccepted,
    },

    {
      icon: <DialpadIcon />,
      label: "DTMF",
      onClick: () => openDialpad("dialpad"),
      disabled: !isCallAccepted, //enabling this even before call is accepted to bypass spam filter.
    },

    {
      icon: isTransferTarget ? (
        <CloseIcon />
      ) : isMultipleCalls ? (
        <CallSplitIcon />
      ) : (
        <PersonAddIcon />
      ),
      label: isTransferTarget
        ? "Cancel Transfer"
        : isMultipleCalls
          ? "Merge Calls"
          : "Add call",
      onClick: isTransferTarget
        ? onCancelTransfer
        : isMultipleCalls
          ? onMergeCalls
          : () => openDialpad("addCall"),
      disabled: !isCallAccepted,
    },

    {
      icon: <SwapCallsIcon />,
      label: isTransferTarget ? "Complete Transfer" : "Transfer",
      onClick: isTransferTarget
        ? onCompleteTransfer
        : () => openDialpad("transfer"),
      disabled: !isCallAccepted || isConference,
    },

    {
      icon: <LocalParkingIcon />,
      label: "Park Call",
      onClick: () => openParkOverlay(),
      disabled: !isCallAccepted || isConference,
    },

    {
      icon: <CompareArrowsIcon />,
      label: "Swap Calls",
      onClick: onSwapCalls,
      disabled: !isCallAccepted || !isMultipleCalls || isTransferTarget, // Disable if there's only one call or we are completing assisted transfer
    },

    // {
    //   icon: <VisibilityOffIcon />,
    //   label: "Hide call",
    //   onClick: toggleMinimize,
    //   disabled: !isCallAccepted,
    // },
    {
      icon: <GraphicEqIcon />,
      label: isRealtime ? "Stop AI Assistant"
        : isStartingRealtime ? "Starting..."
          : "Start AI Assistant",
      onClick: onRealtimeToggle,
      isActive: isRealtime || isStartingRealtime,
      // disabled: !isCallAccepted || !callAssistantEnabled || isQuotaExceeded,
      disabled: isStartingRealtime || !callAssistantEnabled || isQuotaExceeded,
      tooltipMessage: getAiAssistantTooltip(),
    },

    {
      icon: <FiberManualRecordIcon />,
      label: isRecording ? "Stop Recording" : "Start Recording",
      onClick: onRecordingToggle,
      isActive: isRecording,
      disabled: !isCallAccepted || isConference,
    },
  ];

  return (
    <React.Fragment>
      {isVisible && !isMinimized && (
        <Draggable
          nodeRef={nodeRef}
          position={position}
          onDrag={onDrag}
          bounds={{
            left: drawerWidth,
            top: appBarHeight,
            right: window.innerWidth - 615,
            bottom: window.innerHeight - 750,
          }}
        >
          <Box
            ref={nodeRef}
            sx={{
              position: "fixed",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              mt: 4,
              display: isMinimized ? "none" : "block",
            }}
          >
            <Container maxWidth="sm">
              <Box
                className="drag-handle"
                sx={{
                  p: 2.5,
                  // backgroundColor: "background.paper",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.background.paper, 0.6),
                  backdropFilter: "blur(16px)",
                  borderRadius: 4,
                  // width: '30rem',
                  width: "23rem",
                  position: "relative",
                  cursor: "move",
                }}
              >
                <MinimizeButton onClick={toggleMinimize}>
                  <RemoveIcon fontSize="small" />
                </MinimizeButton>
                <Box position="relative" height="170px">
                  <Box position="absolute" width="100%">
                    <Fade in={!isConference} timeout={500}>
                      <Box>
                        <CallHeader
                          callSessions={callSessions}
                          activeCall={activeCall}
                        />
                      </Box>
                    </Fade>
                  </Box>
                  <Box position="absolute" width="100%">
                    <Fade in={isConference} timeout={500}>
                      <Box>
                        <ConferenceCallHeader
                          callSessions={callSessions}
                          activeCall={activeCall}
                        />
                      </Box>
                    </Fade>
                  </Box>
                </Box>

                {activeCall?.crmContact && (() => {
                  const crm = getCrmMeta(crmType, crmCatalog[crmType]);
                  const contact = activeCall.crmContact;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, py: 0.75, mb: 0.5, borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.08)}` }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: crm?.primary, flexShrink: 0 }} />
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                        {contact.name}
                        {contact.company && (
                          <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 0.5 }}>
                            · {contact.company}
                          </Box>
                        )}
                      </Typography>
                      {crm && (
                        <Typography variant="caption" sx={{ color: crm.primary, fontWeight: 600, fontSize: "0.62rem", letterSpacing: "0.04em", textTransform: "uppercase", flexShrink: 0 }}>
                          {crm.label}
                        </Typography>
                      )}
                      <Tooltip title="View contact details">
                        <IconButton size="small" onClick={onCrmPanelToggle} sx={{ p: 0.25, color: crmPanelOpen ? crm?.primary : "text.secondary" }}>
                          <OpenInFullIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })()}

                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                >
                  {buttonConfig.map((item, index) => (
                    <Grid item xs={4} key={index} sx={{ textAlign: "center" }}>
                      <Tooltip
                        title={item.tooltipMessage || ""}
                        arrow
                        placement="top"
                      >
                        <span>
                          <StyledButton
                            fullWidth
                            onClick={item.onClick}
                            isActive={item.isActive}
                            disabled={item.disabled}
                          >
                            {item.icon}
                          </StyledButton>
                        </span>
                      </Tooltip>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {item.label}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ position: "relative", height: "80px", mt: 0 }}>
                  <CancelButton onClick={onCallEnd}>
                    <CallEndIcon />
                  </CancelButton>
                </Box>
              </Box>
            </Container>
          </Box>
        </Draggable>
      )}

      {isVisible && isMinimized && (
        <MinimizedContainer onClick={toggleMinimize}>
          <CallEndIcon sx={{ mr: 1, color: "error.main" }} />
          <Typography variant="body2" sx={{ mr: 1 }}>
            Active Call
          </Typography>
          <OpenInFullIcon fontSize="small" />
        </MinimizedContainer>
      )}

      <Dialog
        open={isDialpadOpen}
        onClose={closeDialpad}
        sx={{
          borderRadius: 2,
          "& .MuiDialog-paper": {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <DialpadOverlay
          onClose={closeDialpad}
          mode={dialpadMode}
          onAssistedTransfer={onAssistedTransfer}
        />
      </Dialog>

      <Dialog
        open={isParkOverlayOpen}
        onClose={closeParkOverlay}
        sx={{
          borderRadius: 2,
          "& .MuiDialog-paper": {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <CallParkOverlay onClose={closeParkOverlay} />
      </Dialog>


      {isVisible && !isMinimized && crmPanelOpen && activeCall?.crmContact && (
        <Draggable
          nodeRef={crmCardRef}
          cancel="button, a, .MuiIconButton-root, .MuiLink-root"
          defaultPosition={{ x: drawerWidth + 400, y: appBarHeight + 100 }}
          bounds={{ left: 0, top: appBarHeight, right: window.innerWidth - 290, bottom: window.innerHeight - 200 }}
        >
          <Box ref={crmCardRef} sx={{ position: "fixed", zIndex: (t) => t.zIndex.drawer + 1, cursor: "move" }}>
            <CrmContactCard
              contact={activeCall.crmContact}
              crmType={crmType}
              onClose={onCrmPanelToggle}
            />
          </Box>
        </Draggable>
      )}

      <FloatingAssistantOverlay
        events={aiAssistantEvents}
        drawerWidth={drawerWidth}
        appBarHeight={appBarHeight}
        onSendPrompt={onSendPrompt}
      />

      <LeadContextPanel
        leadContext={leadContext}
        drawerWidth={drawerWidth}
        appBarHeight={appBarHeight}
      />

    </React.Fragment>
  );
};

export default withActiveCallManagement(ActiveCallScreen);
